import { SeeqNames } from '@/main/app.constants.seeqnames';
import { sqContextApi } from '@/sdk';
import { AnyProperty } from '@/utilities.types';
import { camelToSnake } from '@/utilities/stringHelper.utilities';
import { randomUUID, validateGuid } from '@/utilities/utilities';
import { UserDefinedContextType } from '@/vantage/components/extensionConfig/extensionConfig.types';
import { MAX_POSTGRES_EPOCH_MILLISEC, PROTECTED_CATEGORIES, VantageNames } from '@/vantage/constants';
import * as graphql from '@/vantage/graphql';
import * as labels from '@/vantage/labels';
import { ContextContent, ContextSchema, MinimalContext, Related, StagedContext } from '@/vantage/types/Context.types';
import { ContextCharacteristic, ExtensionSchema } from '@/vantage/types/Extension.types';
import { Row, SimplifiedNode } from '@/vantage/types/SimplifiedNode.types';
import {
  ensureDate,
  ensureIso,
  isMaxEnd,
  isUserDefinedContextName,
  parseAsCamel,
  splitUserDefinedContextName,
} from '@/vantage/utilities';
import _ from 'lodash';

export type ContextPredicate = (context: ContextSchema) => boolean;

export const noContextPredicate = () => true;

interface FilterPass {
  excludedCount: number;
  contexts: ContextSchema[];
}

export const filterPasses = (
  contexts: ContextSchema[],
  prefilter: ContextPredicate,
  predicates: ContextPredicate[],
): FilterPass[] => {
  const passes: FilterPass[] = [];
  let unfilteredContexts = contexts;
  predicates.forEach((predicate) => {
    const filteredContexts = unfilteredContexts.filter(
      (context: ContextSchema) => !prefilter(context) || predicate(context),
    );
    passes.push({ excludedCount: unfilteredContexts.length - filteredContexts.length, contexts: filteredContexts });
    unfilteredContexts = filteredContexts;
  });
  return passes;
};

function contextFilter(
  context: ContextSchema,
  characteristic: ContextCharacteristic,
  extension: ExtensionSchema,
): boolean {
  return (
    context.kind === characteristic.kind &&
    [characteristic.proxy_identifier, extension.name].includes(context.identifier)
  );
}

export function nonEvidenceContextMetadataFilters(
  extensions: ExtensionSchema[],
): ((context: ContextSchema) => boolean)[] {
  return extensions.flatMap(
    (extension) =>
      extension.contextCharacteristics
        ?.filter(
          (characteristic) => characteristic.kind !== VantageNames.Kinds.Capsule && characteristic.proxyKind !== 'leaf',
        )
        .map((characteristic) => {
          return (context: ContextSchema) => contextFilter(context, characteristic, extension);
        }) ?? [],
  );
}

export function altKeys(item: any, key1: string, ...otherKeys: string[]): any {
  const allKeys = [key1, ...otherKeys];
  // Look for keys as is
  for (const key of allKeys) {
    if (item[key]) {
      return item[key];
    }
  }
  // Look for keys in the source table
  const sourceTable = item.sourceTable ?? 'context_opaque';
  for (let key of allKeys) {
    key = `${sourceTable}.${camelToSnake(key)}`;
    if (item[key]) {
      return item[key];
    }
  }
}

function parseRelated(related: string | undefined): Related[] {
  if (!related) {
    return [];
  }
  return related.split(',').map((s: string) => {
    const i = s.lastIndexOf(':');
    return { capsuleId: s.substring(0, i), identifier: s.substring(i) };
  });
}

function contentFromSourceContext(context: any): ContextContent {
  const contentValue = altKeys(
    context,
    VantageNames.ContextQueryContextColumns.ContextGraphqlLabelName,
    VantageNames.TableQueryContextColumns.ContextLabelsLabelName,
    VantageNames.ContextQueryContextColumns.ContextGraphqlCommentsComment,
    VantageNames.TableQueryContextColumns.ContextCommentsComment,
    VantageNames.ContextQueryContextColumns.ContextGraphqlNumericValue,
    VantageNames.TableQueryContextColumns.ContextNumericValue,
  );
  return {
    value: contentValue ?? null,
  };
}

const categoryName = (context: ContextSchema) =>
  altKeys(
    context,
    VantageNames.ContextQueryContextColumns.ContextGraphqlCategoryName,
    VantageNames.ContextQueryContextColumns.ContextGraphqlNumericDescriptionName,
    VantageNames.TableQueryContextColumns.ContextLabelsCategoryName,
  );

const labelName = (context: ContextSchema) =>
  altKeys(
    context,
    VantageNames.ContextQueryContextColumns.ContextGraphqlLabelName,
    VantageNames.TableQueryContextColumns.ContextLabelsLabelName,
  );

function identifierFromSourceContext(context: any, reasonCodeLabelIds?: string[]): string {
  const category = categoryName(context);

  if (!category) {
    return VantageNames.Extensions.Notes;
  }
  const labelId = altKeys(context, 'context', VantageNames.TableQueryContextColumns.ContextLabelsLabelId);
  if (reasonCodeLabelIds?.includes(labelId)) {
    return VantageNames.Extensions.ReasonCodes;
  }

  return category
    .replaceAll(`${SeeqNames.SeeqContextNameSpace.VantageLabelCategoryNamespace}.`, '')
    .replaceAll(SeeqNames.SeeqContextNameSpace.UsageRestrictedLabelNamespace, '')
    .replaceAll(`${SeeqNames.Types.Vantage}.`, '');
}

function kindFromSourceContext(context: any, identifier: string, datumId?: string): string {
  if (context?.isCapsuleContext) {
    return VantageNames.Kinds.Capsule;
  }
  const itemType = context?.item?.type ?? context[VantageNames.TableQueryContextColumns.ItemType];
  if (!itemType) {
    throw new Error('Unable to find type of the itemId pertaining to context');
  }
  if (itemType.includes(VantageNames.Columns.Condition)) {
    // Inferring the distinction between capsule and condition context kind may become brittle as the usage of
    // context and capsule IDs expands, see CRAB-51288
    return !datumId || identifier === VantageNames.Extensions.Suppression
      ? VantageNames.Kinds.Condition
      : VantageNames.Kinds.Capsule;
  }
  return itemType;
}

function userMetadataFromSourceContext(context: any): { name: string; id: string | null } {
  const userMetadata = context?.createdBy;
  return {
    name: userMetadata?.fullName ?? altKeys(context, VantageNames.TableQueryContextColumns.CreatorName),
    id: userMetadata?.guid ?? altKeys(context, 'creator_id') ?? null,
  };
}

const purviewFromSourceContext = (itemId: string, kind: string): Record<string, string> | undefined =>
  kind === VantageNames.Kinds.Capsule ? undefined : { [kind]: itemId };

function formatContext(context: any, metadata: any, reasonCodeLabelIds?: string[]): ContextSchema {
  const related = parseRelated(metadata?.related);
  const content = contentFromSourceContext(context);
  const identifier = identifierFromSourceContext(context, reasonCodeLabelIds);
  const itemId = altKeys(context, 'itemId', SeeqNames.MaterializedTables.ItemIdColumn);
  const datumId = altKeys(context, 'datumId', SeeqNames.MaterializedTables.DatumIdColumn);
  const kind = kindFromSourceContext(context, identifier, datumId);

  return {
    ...context,
    itemId,
    datumId,
    content,
    context: { ..._.omit(metadata, ['serializedExtensions']), related },
    conditionId: altKeys(context, 'itemId', SeeqNames.MaterializedTables.ItemIdColumn),
    eventEnd: isMaxEnd(altKeys(context, 'endTime')) ? null : ensureDate(altKeys(context, 'endTime')),
    eventStart: ensureDate(altKeys(context, 'startTime')),
    extensionContexts: metadata?.serializedExtensions,
    id: altKeys(context, 'contextId', 'id'),
    identifier,
    kind,
    kindId:
      kind === VantageNames.Kinds.Capsule
        ? altKeys(context, 'datumId', SeeqNames.MaterializedTables.DatumIdColumn)
        : altKeys(context, 'itemId', SeeqNames.MaterializedTables.ItemIdColumn),
    messageTime: altKeys(context, 'createdAt'),
    related,
    metadataId: metadata?.id,
    purview: purviewFromSourceContext(itemId, kind),
    seeqUser: userMetadataFromSourceContext(context).name,
    isPinned: context.isPinned,
    timeCreated: ensureDate(altKeys(context, 'createdAt')),
    [VantageNames.ContextFormattingFields.TimeUpdated]: ensureDate(altKeys(context, 'updatedAt')),
    userMetadata: userMetadataFromSourceContext(context),
    userSeeqId: userMetadataFromSourceContext(context).id,
    category: categoryName(context),
    labelName: labelName(context),
    archived: altKeys(context, 'archived'),
  };
}

function indexByFieldAndAddSourceTable(
  items: Record<string, string>[],
  indexBy: string,
  source: string,
): Record<string, Record<any, any>> {
  return _.keyBy(items, (item) => {
    item.sourceTable = source;
    return item[indexBy];
  });
}

export function combineSourceContextGraphql(comments: any[], labels: any[], numerics: any[]): any {
  const commentsById = indexByFieldAndAddSourceTable(
    comments,
    VantageNames.ContextQueryContextColumns.ContextGraphqlContextId,
    VantageNames.Tables.ContextComments,
  );
  const labelsById = indexByFieldAndAddSourceTable(
    labels,
    VantageNames.ContextQueryContextColumns.ContextGraphqlContextId,
    VantageNames.Tables.ContextLabels,
  );
  const numericsById = indexByFieldAndAddSourceTable(
    numerics,
    VantageNames.ContextQueryContextColumns.ContextGraphqlContextId,
    VantageNames.Tables.ContextNumeric,
  );

  return { ...commentsById, ...labelsById, ...numericsById };
}

export function combineSourceContextMt(comments: any[], labels: any[], numerics: any[]): any {
  const commentsById = indexByFieldAndAddSourceTable(
    comments,
    VantageNames.TableQueryContextColumns.ContextCommentsId,
    VantageNames.Tables.ContextComments,
  );

  const labelsById = indexByFieldAndAddSourceTable(
    labels,
    VantageNames.TableQueryContextColumns.ContextLabelsId,
    VantageNames.Tables.ContextLabels,
  );

  const numericsById = indexByFieldAndAddSourceTable(
    numerics,
    VantageNames.TableQueryContextColumns.ContextNumericId,
    VantageNames.Tables.ContextNumeric,
  );

  return { ...commentsById, ...labelsById, ...numericsById };
}

function minimalContext(context: any): MinimalContext {
  return {
    itemId: altKeys(context, 'itemId', SeeqNames.MaterializedTables.ItemIdColumn),
    datumId: altKeys(context, 'datumId', SeeqNames.MaterializedTables.DatumIdColumn),
    contextId: altKeys(context, 'contextId', 'id'),
    endTime: altKeys(context, 'endTime'),
    startTime: altKeys(context, 'startTime'),
    createdAt: altKeys(context, 'createdAt'),
    updatedAt: altKeys(context, 'updatedAt'),
    archived: altKeys(context, 'archived'),
    createdBy: context?.createdBy,
    isCapsuleContext: context?.isCapsuleContext,
    [VantageNames.TableQueryContextColumns.ItemType]:
      context?.item?.type ?? context[VantageNames.TableQueryContextColumns.ItemType],
  };
}

function metadataBySourceContextId(opaque: any[]): AnyProperty {
  const metadataBySourceId: AnyProperty = {};
  const orphanOpaque: AnyProperty[] = [];

  opaque
    .filter(
      (context) =>
        altKeys(context, 'key', VantageNames.TableQueryContextColumns.ContextOpaqueKey).match(/^vantage_metadata/) &&
        !altKeys(context, 'archived', VantageNames.TableQueryContextColumns.ContextOpaqueArchived),
    )
    .forEach((context) => {
      const metadata = parseAsCamel(
        altKeys(context, 'context', VantageNames.TableQueryContextColumns.ContextOpaqueData),
      );
      metadata['id'] = altKeys(context, 'contextId', 'id');
      const sourceContextIds = Array.isArray(metadata.seeqContextIds) ? metadata.seeqContextIds : [];
      if (sourceContextIds.length === 0) {
        // If there are no seeq context ids, then this is an orphan opaque context, use a minimal version of the opaque
        // context itself as the source context and append it to the orphanOpaque list
        metadataBySourceId[altKeys(context, 'contextId', 'id')] = metadata;
        orphanOpaque.push(minimalContext(context));
      }
      sourceContextIds.forEach((id: string) => {
        metadataBySourceId[id] = metadata;
      });
    });

  return { metadataBySourceId, orphanOpaque };
}

export function contextWithMetadata(opaque: any[], sourceContext: any, reasonCodeLabelIds?: string[]): any[] {
  const { metadataBySourceId, orphanOpaque } = metadataBySourceContextId(opaque);

  const formattedSourceContext = Object.entries(sourceContext).map(([contextId, context]) =>
    formatContext(context, metadataBySourceId[contextId] || null, reasonCodeLabelIds),
  );

  // We can remove this portion once we replace all opaque fields with calculations based on comments and labels
  const formattedOrphanOpaque = orphanOpaque.map((opaque: any) =>
    formatContext(opaque, metadataBySourceId[altKeys(opaque, 'contextId', 'id')] || null),
  );

  return [...formattedSourceContext, ...formattedOrphanOpaque];
}

export enum ContextOperation {
  Add = 'Add',
  Resolve = 'Resolve',
  Delete = 'Delete',
}

export function getContextOperation(
  stagedContext: StagedContext,
  characteristic: ContextCharacteristic,
): ContextOperation {
  if (stagedContext.deletionIds) {
    return characteristic?.eventTimeRepresentation === 'investigationTime'
      ? ContextOperation.Resolve
      : ContextOperation.Delete;
  }
  return ContextOperation.Add;
}

const getLabelId = async (categoryName: string, labelName: string): Promise<string | null> => {
  const category = await labels.findOrCreateLabelCategory(categoryName);
  const label = category?.id
    ? await labels.findOrCreateLabel({ categoryId: category.id, name: labelName }, true)
    : null;
  return label?.id ?? null;
};

const getVantageLabelId = (ctx: StagedContext): Promise<string | null> => {
  let categoryName = ctx.identifier;
  if (!isUserDefinedContextName(categoryName)) {
    const prefix = PROTECTED_CATEGORIES.includes(ctx.identifier)
      ? SeeqNames.SeeqContextNameSpace.VantageLabelCategoryNamespace
      : 'Vantage';
    categoryName = `${prefix}.${ctx.identifier}`;
  }
  return getLabelId(
    categoryName,
    typeof ctx.content.value === 'boolean' ? ctx.identifier : (ctx.content.value as string),
  );
};

const getKindId = (node: SimplifiedNode | Row) => {
  if (!node.selectedRow) {
    return (node as Row)[VantageNames.Columns.DatumID];
  } else if (node.kind === VantageNames.Kinds.Capsule) {
    return (node.selectedRow as Row)[VantageNames.Columns.DatumID];
  } else if (node.kind === VantageNames.Kinds.Condition) {
    return (node.selectedRow as Row)[VantageNames.Columns.ConditionID];
  } else return '';
};

export const canCreateContext = (node: SimplifiedNode): boolean => {
  return (
    node.kind === VantageNames.Kinds.Condition ||
    node.kind === VantageNames.Kinds.Capsule ||
    node.kind === VantageNames.Kinds.Asset
  );
};

export const getItemId = (node: SimplifiedNode | Row) => {
  if (!node.kind) {
    return (node as Row)[VantageNames.Columns.ConditionID];
  }
  if (node.creationPurview?.[node.kind]) {
    return node.creationPurview[node.kind];
  }
  if (node.kind === VantageNames.Kinds.Asset) {
    return (node.selectedRow as Row)[VantageNames.Columns.AssetUUID];
  }
  return [VantageNames.Kinds.Capsule, VantageNames.Kinds.Condition].includes(node.kind)
    ? (node.selectedRow as Row)[VantageNames.Columns.ConditionID]
    : null;
};

export const getDatumId = (node: SimplifiedNode | Row): string | null => {
  if (!node.kind) {
    return (node as Row)[VantageNames.Columns.DatumID];
  } else if (node.kind === VantageNames.Kinds.Capsule) {
    return (node.selectedRow as Row)[VantageNames.Columns.DatumID];
  } else {
    return null;
  }
};

export const getContextIdPair = async (node: SimplifiedNode | Row): Promise<[string | null, string | null]> => {
  const datumId = getDatumId(node);
  const itemId = getItemId(node);
  if (validateGuid(itemId)) return [itemId, datumId];
  throw new Error(`Could not find item ID and datum ID for node ${node}`);
};

export const itemIdFromContext = (context: ContextSchema) => {
  if ([VantageNames.Kinds.Capsule, VantageNames.Kinds.Condition].includes(context.kind)) {
    return context.conditionId;
  }
  const purviewId = context.purview[context.kind];
  if (purviewId) {
    return purviewId;
  }
  throw new Error(`Could not determine item ID for context ${context}`);
};

export const datumIdFromContext = (context: ContextSchema): string | undefined =>
  context.kind === VantageNames.Kinds.Capsule ? context.kindId : undefined;

export function itemIdField(field: string, contextItemIdMapping?: Record<string, string>) {
  return contextItemIdMapping?.[field] ?? field;
}

const getEventTime = (node: SimplifiedNode, characteristic: ContextCharacteristic) => {
  switch (characteristic?.eventTimeRepresentation) {
    case 'investigationTime':
      return [new Date().toISOString(), new Date(MAX_POSTGRES_EPOCH_MILLISEC).toISOString()];
    case 'capsuleRange':
      return [null, null]; // TO DO : need to implement as it currently is in python... there are some subtle differences
    case 'capsule':
      return [
        node.selectedRow[VantageNames.Columns.Start],
        node.selectedRow[VantageNames.Columns.End] || new Date().toISOString(),
      ];
    default:
      return [null, null];
  }
};

export const getReasonCodeEventTime = (context: StagedContext, datumId: string) => {
  return [context.content.payload.windows[datumId].start, context.content.payload.windows[datumId].end];
};

interface LabelInterface {
  node: SimplifiedNode | Row;
  ctx: StagedContext;
  itemId: string;
  datumId: string | null;
  start: string | null;
  end: string | null;
  user: any;
  labelId: string;
  conditionMonitorId: string;
}

interface NumericInterface {
  node: SimplifiedNode | Row;
  ctx: StagedContext;
  itemId: string;
  datumId: string | null;
  start: string | null;
  end: string | null;
  user: any;
  conditionMonitorId: string;
}

interface NoteInterface {
  node: SimplifiedNode | Row;
  ctx: StagedContext;
  itemId: string;
  datumId: string | null;
  start: string | null;
  end: string | null;
  related: string;
  serializedContext: StagedContext[];
  user: any;
  conditionMonitorId: string;
}

interface ContextMetadata {
  identifier: string;
  kind: string;
  kind_id: string;
  seeq_context_ids: string[];
  related?: string;
  content: unknown;
  serialized_extensions: unknown[];
  user_metadata: { name: string; id: string };
  purview: Record<string, string>;
  conditionMonitorId: string;
}

interface ContextMetadataInput {
  itemId: string;
  datumId: string | null;
  startTime: string | null;
  endTime: string | null;
  metadata: ContextMetadata;
}

function createContextMetadata({ itemId, datumId, metadata, startTime, endTime }: ContextMetadataInput) {
  return sqContextApi
    .createContextOpaque(
      {
        datumId: datumId ?? undefined,
        key: `vantage_metadata${randomUUID()}`,
        data: JSON.stringify(metadata),
        startTime: startTime ?? undefined,
        endTime: endTime ?? undefined,
      },
      {
        itemId,
      },
    )
    .then(({ data }) => data);
}

const addLabel = async ({
  node,
  ctx,
  itemId,
  datumId,
  start,
  end,
  user,
  labelId,
  conditionMonitorId,
}: LabelInterface) => {
  const { data: label } = await sqContextApi.createContextLabel(
    {
      datumId: datumId ?? undefined,
      labelId,
      startTime: start ?? undefined,
      endTime: end ?? undefined,
    },
    { itemId },
  );
  const metadata: ContextMetadata = {
    identifier: ctx.identifier,
    kind: node.kind || VantageNames.Kinds.Capsule,
    kind_id: getKindId(node),
    seeq_context_ids: [label.contextId!],
    content: ctx.content,
    serialized_extensions: [],
    user_metadata: { name: user.name, id: user.id },
    purview: node.creationPurview || {},
    conditionMonitorId,
  };
  return createContextMetadata({ itemId, datumId, metadata, startTime: start, endTime: end });
};

const addNumeric = async ({ node, ctx, itemId, datumId, start, end, user, conditionMonitorId }: NumericInterface) => {
  const { data: numeric } = await sqContextApi.createContextNumeric(
    {
      datumId: datumId ?? undefined,
      numericDescriptionId: ctx.content.payload.numericDescription.id,
      value: Number(ctx.content.value),
      startTime: start ?? undefined,
      endTime: end ?? undefined,
    },
    { itemId },
  );
  const metadata: ContextMetadata = {
    identifier: ctx.identifier,
    kind: node.kind || VantageNames.Kinds.Capsule,
    kind_id: getKindId(node),
    seeq_context_ids: [numeric.contextId!],
    content: ctx.content,
    serialized_extensions: [],
    user_metadata: { name: user.name, id: user.id },
    purview: node.creationPurview || {},
    conditionMonitorId,
  };
  return createContextMetadata({ itemId, datumId, metadata, startTime: start, endTime: end });
};

export const addNote = async ({
  node,
  ctx,
  itemId,
  datumId,
  start,
  end,
  related,
  serializedContext,
  user,
  conditionMonitorId,
}: NoteInterface) => {
  const { data: comment } = ctx.content.value
    ? await sqContextApi.createContextComment(
        {
          datumId: datumId ?? undefined,
          comment: ctx.content.value as string,
          startTime: start ?? undefined,
          endTime: end ?? undefined,
        },
        { itemId },
      )
    : { data: null };
  const metadata: ContextMetadata = {
    identifier: ctx.identifier,
    kind: node.kind,
    kind_id: getKindId(node),
    seeq_context_ids: comment ? [comment.contextId!] : [],
    related,
    content: ctx.content,
    serialized_extensions: serializedContext.filter((item) => item.identifier !== VantageNames.Extensions.Notes),
    user_metadata: { name: user.name, id: user.id },
    purview: node.creationPurview,
    conditionMonitorId,
  };
  return createContextMetadata({ itemId, datumId, metadata, startTime: start, endTime: end });
};

function timeKey(start: number | string | Date | null | undefined, end: number | string | Date | null | undefined) {
  const startKey = start ? ensureDate(start)!.valueOf() : '?';
  const endKey = end ? ensureDate(end)!.valueOf() : '?';
  return `${startKey}-${endKey}`;
}

interface CreateContextParameters {
  ctx: StagedContext;
  characteristic: ContextCharacteristic;
  node: SimplifiedNode;
  serializedRelated: string;
  serializedContext: any;
  user: any;
  existingEvidenceContextKeys: string[];
  activeContext: ContextSchema[];
  itemId: string;
  datumId: string | null;
  conditionMonitorId: string;
}

export const createContext = async ({
  ctx,
  characteristic,
  node,
  serializedRelated,
  serializedContext,
  user,
  existingEvidenceContextKeys,
  activeContext,
  itemId,
  datumId,
  conditionMonitorId,
}: CreateContextParameters) => {
  if (ctx.identifier === VantageNames.Extensions.Suppression) {
    return;
  }

  let [start, end] =
    ctx.identifier === VantageNames.Extensions.ReasonCodes
      ? getReasonCodeEventTime(ctx, datumId!)
      : getEventTime(node, characteristic);

  if (ctx.identifier === VantageNames.Extensions.Notes) {
    return addNote({
      node,
      ctx,
      itemId,
      datumId,
      start,
      end,
      related: serializedRelated,
      serializedContext,
      user,
      conditionMonitorId,
    });
  } else {
    const isUserDefinedNumericContext =
      ctx.content.payload && 'numericDescription' in ctx.content.payload && ctx.content.payload.numericDescription.id;
    if (characteristic.proxyKind === 'leaf') {
      const leafs = node.leafCapsules.filter((leaf) => {
        const leafItemId = getItemId(leaf) || itemId;
        const datumId = getDatumId(leaf);
        return !existingEvidenceContextKeys.includes(`${leafItemId}:${datumId}:${ctx.identifier}:${ctx.content.value}`);
      });
      const labelId = isUserDefinedNumericContext ? null : await getVantageLabelId(ctx);
      await Promise.all(
        leafs.map(async (leaf) =>
          isUserDefinedNumericContext
            ? addNumeric({
                node: leaf,
                ctx,
                itemId: getItemId(leaf) || itemId,
                datumId: getDatumId(leaf),
                start: leaf[VantageNames.Columns.Start],
                end: leaf[VantageNames.Columns.End] || new Date().toISOString(),
                user,
                conditionMonitorId,
              })
            : labelId &&
              addLabel({
                node: leaf,
                ctx,
                itemId: getItemId(leaf) || itemId,
                datumId: getDatumId(leaf),
                start: leaf[VantageNames.Columns.Start],
                end: leaf[VantageNames.Columns.End] || new Date().toISOString(),
                user,
                labelId,
                conditionMonitorId,
              }),
        ),
      );
    } else {
      const evidenceContextMissing = !existingEvidenceContextKeys.includes(
        `${itemId}:${datumId}:${ctx.identifier}:${ctx.content.value}:${timeKey(start, end)}`,
      );
      const nonEvidenceContextMissing = !activeContext
        ?.map(
          (active) =>
            `${itemIdFromContext(active)}:${datumIdFromContext(active)}:${active.content.value}:${timeKey(
              active.eventStart,
              active.eventEnd,
            )}`,
        )
        .includes(`${itemId}:${datumId}:${ctx.content.value}`);
      if (evidenceContextMissing && nonEvidenceContextMissing) {
        if (isUserDefinedNumericContext) {
          return addNumeric({
            node,
            ctx,
            itemId,
            datumId,
            start,
            end,
            user,
            conditionMonitorId,
          });
        } else {
          const labelId = await getVantageLabelId(ctx);
          return addLabel({
            node,
            ctx,
            itemId,
            datumId,
            start,
            end,
            user,
            labelId: labelId!,
            conditionMonitorId,
          });
        }
      } else {
        return () => {
          console.info(
            `Context of type ${ctx.identifier} and content ${ctx.content.value} already exists on itemId: ${itemId} and datumId: ${datumId} - skipping creation...`,
          );
        };
      }
    }
  }
};

export const deleteContext = async (deletionId: string, activeContext: ContextSchema[], archiveChildren = false) => {
  const context = activeContext.find((item) => item.id === deletionId)!;
  if (context.archived) {
    return Promise.resolve([]);
  }
  const { type } = splitUserDefinedContextName(context.identifier) || {};
  const itemId = itemIdFromContext(context);

  const ops = [
    sqContextApi.deleteContextOpaque({ itemId, opaqueId: context.metadataId, _delete: true }),
    type === UserDefinedContextType.Numeric
      ? sqContextApi.deleteContextNumeric({ itemId, numericContextId: context.id })
      : sqContextApi.deleteContextLabel({ itemId, labelId: context.id, archiveChildren }),
  ];

  return Promise.all(ops);
};

export const resolveContext = async (deletionId: string, activeContext: ContextSchema[]) => {
  const context = activeContext
    .filter((ctx) => ctx.identifier !== VantageNames.Extensions.Suppression)
    .find((item) => item.id === deletionId);
  return context ? resolve(context) : null;
};

const resolve = async (context: ContextSchema) => {
  const itemId = itemIdFromContext(context);
  const datumId = datumIdFromContext(context);
  const now = new Date().toISOString();

  const userDefinedContextInfo = splitUserDefinedContextName(context.identifier);
  if (userDefinedContextInfo?.type !== UserDefinedContextType.Numeric) {
    const labelId = await getVantageLabelId(context);
    await sqContextApi.batchCreateContextLabels(
      [
        {
          contextLabelId: context.id,
          datumId: datumId ?? undefined,
          labelId: labelId ?? undefined,
          startTime: context.eventStart ? ensureIso(context.eventStart) : undefined,
          endTime: now,
        },
      ],
      { itemId },
    );
  }

  const {
    contextId: opaqueId,
    context: data,
    key,
    startTime,
  } = await retrieveOpaque(itemId, datumId, context.metadataId);
  const { data: updatedOpaqueContext } = await sqContextApi.updateContextOpaque(
    {
      datumId: datumId ?? undefined,
      key,
      startTime,
      endTime: now,
      data,
    },
    {
      itemId,
      opaqueId,
    },
  );
  return updatedOpaqueContext;
};

const retrieveOpaque = async (itemId: string, datumId: string | undefined, opaqueId: string) => {
  const matches = await graphql.context(itemId, datumId);
  return matches.find((item) => item.contextId === opaqueId)!;
};

export const suppressionOpaqueLabel = (
  label: any,
  creatorName: string,
  conditionName: string,
  suppressionExpiration: string,
) =>
  ({
    archived: false,
    itemId: label.itemId,
    contextId: randomUUID().toUpperCase(),
    startTime: label.startTime,
    endTime: label.endTime,
    key: `vantage_metadata${randomUUID()}`,
    creatorId: label.creatorId,
    createdAt: label.createdAt,
    updatedAt: label.updatedAt,
    context: JSON.stringify({
      identifier: VantageNames.Extensions.Suppression,
      kind: VantageNames.Kinds.Condition,
      kind_id: label.itemId,
      seeq_context_ids: [label.contextId],
      content: {
        value: true,
        payload: {
          parentCondition: label.itemId,
          suppressionExpiration,
          parentConditionName: conditionName,
        },
      },
      serialized_extensions: [],
      userMetadata: { name: creatorName, id: label.creatorId },
      purview: { [VantageNames.Kinds.Condition]: label.itemId },
      conditionMonitorId: label.datumId.toUpperCase(),
    }),
    item: { type: VantageNames.Columns.Condition },
  } as AnyProperty);

export const suppressionOpaqueNote = (label: any, creatorName: string, conditionName: string) => {
  const content = {
    value: false,
    payload: {
      parentCondition: label.itemId,
      parentConditionName: conditionName,
      contextToolTip: `End reason: ${label.label.replaceAll('-', ' ')} | End time: ${label.endTime}`,
    },
  };

  const serializedExtensions = [
    {
      identifier: VantageNames.Extensions.Suppression,
      content,
      deletionIds: [label.contextId],
    },
  ];
  return {
    archived: false,
    itemId: label.itemId,
    contextId: randomUUID().toUpperCase(),
    startTime: label.startTime,
    endTime: label.endTime,
    key: `vantage_metadata${randomUUID().toUpperCase()}`,
    creatorId: label.creatorId,
    createdAt: label.endTime ?? label.createdAt,
    updatedAt: label.endTime ?? label.updatedAt,
    context: JSON.stringify({
      identifier: VantageNames.Extensions.Notes,
      kind: VantageNames.Kinds.Condition,
      kindId: label.itemId,
      content: {
        ...content,
        serializedExtensions,
      },
      serializedExtensions,
      userMetadata: { name: creatorName, id: label.creatorId },
      purview: { [VantageNames.Kinds.Condition]: label.itemId },
      conditionMonitorId: label.datumId.toUpperCase(),
    }),
    item: { type: VantageNames.Columns.Condition },
  } as AnyProperty;
};
